import { stripFolderIdForPayload } from './collectionFolders';
import { kvRowsForApi } from './kvRows';
import { settingsWithProtocol } from './requestProtocolFields';
import { bodyTypeForApi, normalizeRequestBodyMode } from './requestBodyMode';
import { getFormDataFieldsFromRequest } from './multipart';

/**
 * Serialize a GraphQL body object `{ query, variables, operationName }` into
 * the JSON string that gets sent over the wire.  Returned value is always a
 * non-null string (empty string when body is absent).
 */
export function serializeGraphQLBody(body) {
  // Importers (Bruno, Hoppscotch, Postman) hand over the body ALREADY
  // serialized; turning that string into '' lost every imported GraphQL query
  // in a cloud workspace (28.09.2026).
  if (typeof body === 'string') return body;
  if (!body || typeof body !== 'object') return '';
  const { query, variables, operationName } = body;
  if (!query && !variables && !operationName) return '';
  const payload = { query: query || '' };
  if (variables) {
    try {
      payload.variables = JSON.parse(variables);
    } catch {
      // variables string is not valid JSON — send as-is so the server can surface the error
      payload.variables = variables;
    }
  }
  if (operationName) payload.operationName = operationName;
  return JSON.stringify(payload);
}

/** Build JSON body for POST/PUT /api-requests (Headers/Params/Cookies tab rows). */
export function prepareRequestBodyForApi(source, collectionId, name) {
  const { bodyType, rawBodyLanguage } = normalizeRequestBodyMode(source);
  const body_type = bodyTypeForApi(bodyType, rawBodyLanguage);
  const isFormBody = body_type === 'formdata' || body_type === 'urlencoded';
  const { settings: strippedSettings, folder_id } = stripFolderIdForPayload({
    ...source,
    // A form body is sent as rows, never as `body` (below). An imported request
    // may carry its fields only as a serialized `a=1&b=2` string — derive the
    // rows from it, or the fields do not reach the server at all.
    ...(isFormBody && source.formDataFields == null
      ? { formDataFields: getFormDataFieldsFromRequest(source) }
      : {}),
    bodyType: body_type,
    body_type,
    rawBodyLanguage,
  });
  // gRPC / WebSocket fields ride in settings — the API drops unknown keys (requestProtocolFields.js).
  const settings = settingsWithProtocol(strippedSettings, source);
  if (bodyType === 'none') {
    settings.ui_body_mode = 'none';
  } else {
    delete settings.ui_body_mode;
  }

  // Serialize graphql body to JSON string for API storage
  let bodyValue;
  if (body_type === 'graphql') {
    bodyValue = serializeGraphQLBody(source.body);
  } else if (isFormBody) {
    bodyValue = '';
  } else if (bodyType === 'none') {
    bodyValue = '';
  } else {
    bodyValue = source.body || '';
  }

  return {
    name,
    description: source.description || '',
    collection_id: collectionId,
    method: source.method || 'GET',
    url: source.url || '',
    headers: kvRowsForApi(source.headers || []),
    params: kvRowsForApi(source.params || []),
    cookies: kvRowsForApi(source.cookies || []),
    body: bodyValue,
    body_type,
    auth: source.auth || {},
    mock: source.mock ?? null,
    // Ignored by the server until the api_requests.type column exists (Ecto
    // casts an explicit allowlist, so an unknown key is dropped, not an error).
    // Sending it now means the client half needs no second change when it does.
    type: source.type || 'http',
    sort_order: source.sort_order ?? 0,
    settings,
    folder_id,
    pre_request_script: source.preRequestScript ?? '',
    test_script: source.testScript ?? '',
  };
}
